import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useCookieConsent } from '@/hooks/useCookieConsent';

interface CookieEntry {
  name: string;
  provider: string;
  purpose: string;
  lifetime: string;
}

const CookieTable: React.FC<{ category: string; t: TFunction<'common'> }> = ({
  category,
  t,
}) => {
  const cookies = t(`cookie.${category}.cookies`, { returnObjects: true }) as
    | CookieEntry[]
    | string;
  if (!Array.isArray(cookies)) return null;

  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs text-left border-collapse">
        <thead>
          <tr className="text-neutral-500 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-800">
            <th className="pb-1.5 pr-3 font-medium">{t('cookie.tableHeaders.cookie') as string}</th>
            <th className="pb-1.5 pr-3 font-medium">
              {t('cookie.tableHeaders.provider') as string}
            </th>
            <th className="pb-1.5 pr-3 font-medium">
              {t('cookie.tableHeaders.purpose') as string}
            </th>
            <th className="pb-1.5 font-medium">{t('cookie.tableHeaders.lifetime') as string}</th>
          </tr>
        </thead>
        <tbody>
          {cookies.map((c) => (
            <tr
              key={c.name}
              className="border-b border-neutral-50 dark:border-neutral-800/50 text-neutral-600 dark:text-neutral-400"
            >
              <td className="py-1.5 pr-3 font-mono text-[11px]">{c.name}</td>
              <td className="py-1.5 pr-3">{c.provider}</td>
              <td className="py-1.5 pr-3">{c.purpose}</td>
              <td className="py-1.5 whitespace-nowrap">{c.lifetime}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const CookieBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const { consent, showBanner, setShowBanner, acceptAll, declineAll, saveCustom } =
    useCookieConsent();
  const [expanded, setExpanded] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  // Sync local toggle state with stored consent when banner reopens
  useEffect(() => {
    if (showBanner && consent) {
      setAnalyticsEnabled(consent.analytics);
      setMarketingEnabled(consent.marketing);
    }
  }, [showBanner, consent]);

  // Listen for footer "Cookie Settings" trigger
  useEffect(() => {
    const handler = () => {
      setExpanded(false);
      setShowBanner(true);
    };
    window.addEventListener('verdea:open-cookie-settings', handler);
    return () => window.removeEventListener('verdea:open-cookie-settings', handler);
  }, [setShowBanner]);

  const handleAcceptAll = () => {
    acceptAll();
    setExpanded(false);
  };

  const handleDeclineAll = () => {
    declineAll();
    setExpanded(false);
  };

  const handleSaveCustom = () => {
    saveCustom(analyticsEnabled, marketingEnabled);
    setExpanded(false);
  };

  const bannerContent = (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-[99998] bg-white dark:bg-[#0a0a1a] border-t border-neutral-200 dark:border-neutral-800 shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie consent"
        >
          <div className="max-w-4xl mx-auto px-6 py-5">
            {/* Compact view */}
            {!expanded && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <Cookie className="h-5 w-5 text-neutral-500 dark:text-neutral-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    {t('cookie.description')}{' '}
                    <Link
                      to="/privacy"
                      className="underline text-neutral-800 dark:text-neutral-200 hover:text-black dark:hover:text-white transition-colors"
                    >
                      {t('cookie.privacyPolicy')}
                    </Link>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeclineAll}
                    className="text-xs border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    {t('cookie.declineAll')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExpanded(true)}
                    className="text-xs border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 gap-1"
                  >
                    {t('cookie.customize')}
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAcceptAll}
                    className="text-xs bg-neutral-900 hover:bg-neutral-700 dark:bg-white dark:hover:bg-neutral-200 text-white dark:text-neutral-900"
                  >
                    {t('cookie.acceptAll')}
                  </Button>
                </div>
              </div>
            )}

            {/* Expanded customize view */}
            {expanded && (
              <div className="max-h-[70vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    {t('cookie.preferencesTitle')}
                  </h2>
                  <button
                    onClick={() => setExpanded(false)}
                    className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors p-1 rounded"
                    aria-label="Collapse cookie preferences"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4 mb-5">
                  {/* Strictly Necessary */}
                  <div className="pb-4 border-b border-neutral-100 dark:border-neutral-800">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                            {t('cookie.necessary.title')}
                          </span>
                          <span className="text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 rounded-full">
                            {t('cookie.necessary.alwaysActive')}
                          </span>
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                          {t('cookie.necessary.description')}
                        </p>
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                          {t('cookie.necessary.legalBasis')}
                        </p>
                      </div>
                      <div className="shrink-0 pt-0.5">
                        <Switch checked={true} disabled aria-label="Strictly necessary cookies" />
                      </div>
                    </div>
                    <CookieTable category="necessary" t={t} />
                  </div>

                  {/* Performance & Analytics */}
                  <div className="pb-4 border-b border-neutral-100 dark:border-neutral-800">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 mb-1">
                          {t('cookie.analytics.title')}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                          {t('cookie.analytics.description')}
                        </p>
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                          {t('cookie.analytics.legalBasis')}
                        </p>
                      </div>
                      <div className="shrink-0 pt-0.5">
                        <Switch
                          checked={analyticsEnabled}
                          onCheckedChange={setAnalyticsEnabled}
                          aria-label="Performance and analytics cookies"
                        />
                      </div>
                    </div>
                    <CookieTable category="analytics" t={t} />
                  </div>

                  {/* Marketing */}
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 mb-1">
                          {t('cookie.marketing.title')}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                          {t('cookie.marketing.description')}
                        </p>
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                          {t('cookie.marketing.legalBasis')}
                        </p>
                      </div>
                      <div className="shrink-0 pt-0.5">
                        <Switch
                          checked={marketingEnabled}
                          onCheckedChange={setMarketingEnabled}
                          aria-label="Marketing cookies"
                        />
                      </div>
                    </div>
                    <CookieTable category="marketing" t={t} />
                  </div>
                </div>

                {/* Expanded action buttons */}
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeclineAll}
                    className="text-xs border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    {t('cookie.declineAll')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveCustom}
                    className="text-xs border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    {t('cookie.savePreferences')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAcceptAll}
                    className="text-xs bg-neutral-900 hover:bg-neutral-700 dark:bg-white dark:hover:bg-neutral-200 text-white dark:text-neutral-900"
                  >
                    {t('cookie.acceptAll')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(bannerContent, document.body) : null;
};
